import React, { useState } from "react";
import Head from "next/head";
import { Document, Page, pdfjs } from "react-pdf";
import { PDFDocument, degrees } from "pdf-lib";
import { BiRotateRight } from "react-icons/bi";
import { FaFileUpload, FaDownload } from "react-icons/fa";
import styles from "../../signature.module.css";
import Blog from "../../../../components/CalculatorBlogs/Blog";
import SignaturePopup from "../../../../components/SignaturePopup";
import PdfToolsLinks from "../../../../components/PdfToolsLinks";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const BlogData = [
    {
        id: 1,
        question: "What is PDF Rotate Tool?",
        answer: (
            <div className="">
                <p className="">
                    PDF Rotate Tool is a free online tool that allows you to rotate PDF pages online.
                    You can rotate individual pages or all pages of a PDF file.
                </p>
            </div>
        ),
    },
    {
        id: 2,
        question: "How to Rotate PDF Pages Online?",
        answer: (
            <div className="">
                <p className="">
                    To rotate PDF pages online, you need to upload the PDF you want to rotate.
                    Then, you can rotate individual pages or all pages of the PDF. Finally, you can
                    download the rotated PDF.
                </p>
            </div>
        ),
    },
    {
        id: 3,
        question: "How to rotate individual pages of a PDF?",
        answer: (
            <div className="">
                <p className="">
                    To rotate individual pages of a PDF, you need to upload the PDF you want to rotate.
                    Then, you can rotate individual page by clicking on the preview of the page.
                    Finally, you can download the rotated PDF.
                </p>
            </div>
        ),
    },
    {
        id: 4,
        question: "What other features does PDF Rotate Tool offer?",
        answer: (
            <div className="">
                <p className="">
                    In addition to roatating PDFs, online PDF rotate tools may offer other
                    features, such as: <br />
                    <ul className="list-disc list-inside">
                        <li>Merging PDFs</li>
                        <li>Compressing PDFs</li>
                        <li>Converting PDFs to other formats</li>
                        <li>Converting other formats to PDFs</li>
                        <li>Splitting PDFs</li>
                    </ul>
                </p>
            </div>
        ),
    },
    {
        id: 5,
        question: "Is PDF Rotate Tool free?",
        answer: (
            <div className="">
                <p className="">
                    Yes, PDF Rotate Tool is free. You can rotate PDF pages online for free.
                </p>
            </div>
        ),
    },
];

const RotatePdf = () => {
    const [pdfFile, setPdfFile] = useState(null);
    const [numberOfPages, setNumberOfPages] = useState(null);
    const [loader, setLoader] = useState(false);
    const [rotatePages, setRotatePages] = useState({})
    const [signaturePopup, setSignaturePopup] = useState(false);

    const scrollToPreview = () => {
        const element = document.getElementById("preview-pdfs");
        if (element) {
            element.scrollIntoView({ behavior: "smooth" });
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        setPdfFile(file);
        if (file) {
            scrollToPreview();
        }
    };

    const handlePdfUpload = async (e) => {
        const file = e.target.files[0];
        setPdfFile(file);
        if (file) {
            scrollToPreview();
        }

    };

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumberOfPages(numPages);
        setRotatePages(Array.from(new Array(numPages), (el, index) => index + 1).reduce(
            (acc, cur) => ({ ...acc, [cur]: 0 }),
            {}
        ))
    }

    const handleRotatePDF = async () => {
        if (!pdfFile) return;

        setLoader(true);
        try {
            const pdfBytes = await getPdfBytes(pdfFile);
            const pdfDoc = await PDFDocument.load(pdfBytes);
            const pages = pdfDoc.getPages();
            Object.keys(rotatePages).forEach((pageNumber) => {
                pages[pageNumber - 1].setRotation(degrees(rotatePages[pageNumber]));
            });

            const rotatedPdfBytes = await pdfDoc.save();
            downloadPdf(rotatedPdfBytes);
        } catch (error) {
            console.error("Error rotating PDF: ", error);
            setLoader(false);
        }
    };

    const getPdfBytes = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const pdfBytes = new Uint8Array(event.target.result);
                resolve(pdfBytes);
            };
            reader.onerror = (event) => {
                reject(new Error("Failed to read PDF file."));
            };
            reader.readAsArrayBuffer(file);
        });
    };

    const downloadPdf = (pdfBytes) => {
        const blob = new Blob([pdfBytes], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = "rotated.pdf";
        link.click();
        setLoader(false);
        setSignaturePopup(true);
    };

    const rotatePage = (pageNumber) => {
        setRotatePages({
            ...rotatePages,
            [pageNumber]: rotatePages[pageNumber] === 270 ? 0 : rotatePages[pageNumber] + 90,
        });
    }

    const rotateAllPages = () => {
        setRotatePages(Array.from(new Array(numberOfPages), (el, index) => index + 1).reduce(
            (acc, cur) => ({ ...acc, [cur]: rotatePages[cur] === 270 ? 0 : rotatePages[cur] + 90 }),
            {}
        ))
    }

    return (
        <div className="w-full mt-20 sm:mt-24">
            <Head>
                <meta charSet="utf-8" />
                <title>Rotate PDF Pages | BoloForms</title>
                <meta name="description" content="Rotate PDF pages online for free." />
                <meta name="keywords" content="PDF Rotate Tool, Rotate PDF, PDF Page Rotation, PDF Rotate Online, Rotate PDF Clockwise, Rotate PDF Counterclockwise, Change PDF Orientation, PDF Page Rotator, Rotate and Save PDF, PDF Editing, Document Management" />
                <meta name="author" content="BoloForms" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <link rel="canonical" href="https://www.bolosign.com/signature/pdf-tools/rotate-pdf" />
                <meta property="og:title" content="Rotate PDF Pages | BoloSign" />
                <meta property="og:description" content="Rotate PDF pages online for free." />
                <meta property="og:url" content="https://www.bolosign.com/signature/pdf-tools/rotate-pdf" />
                <meta property="og:site_name" content="BoloSign" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:image:alt" content="Rotate PDF Pages | BoloSign" />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="en_US" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@Bolo_Forms" />
                <meta name="twitter:creator" content="@Bolo_Forms" />
                <meta name="twitter:title" content="Rotate PDF Pages | BoloSign" />
                <meta name="twitter:description" content="Rotate PDF pages online for free." />
            </Head>

            <div className="">
                <div className="max-w-[1280px] mx-auto px-2 md:px-10 py-20 rounded-xl relative flex flex-col gap-20">
                    <div className="text-violet-500 text-center">
                        <h1 className="text-3xl md:text-6xl text-gray-800">
                            Rotate PDF Pages
                        </h1>
                        <p className="text-lg md:text-2xl mt-4 text-gray-600">
                            Rotate individual pages or all pages of a PDF file.
                        </p>
                    </div>
                    <div
                        className="extraOutline p-4 bg-white bg-whtie mx-auto rounded-lg w-full md:w-[600px]"
                        onDragEnter={(e) => e.preventDefault()}
                        onDragOver={(e) => e.preventDefault()}
                        onDragLeave={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                    >
                        <div className="file_upload px-2 md:px-20 py-5 md:py-14 relative border-4 border-dotted border-gray-300 rounded-lg">
                            <FaFileUpload className="text-gray-400 w-20 md:w-32 mx-auto mb-6 md:mb-10 text-[80px] md:text-[120px]" />
                            <div className="input_field flex flex-col w-full mx-auto text-center">
                                <label>
                                    <input
                                        onChange={handlePdfUpload}
                                        className="text-sm cursor-pointer w-36 hidden"
                                        type="file"
                                        accept=".pdf"
                                    />
                                    <div className="text bg-violet-600 text-white text-xl border border-gray-300 rounded-xl font-semibold cursor-pointer py-3 md:py-4 hover:bg-indigo-500">
                                        Upload PDF File
                                    </div>
                                </label>

                                <div className="title text-gray-500 uppercase text-lg md:text-xl">
                                    or drop file here
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    id="preview-pdfs"
                    className="max-w-[1280px] mx-auto flex flex-col gap-14 justify-center items-center"
                >
                    {pdfFile && (
                        <>
                            <h1 className="text-3xl font-light text-center text-gray-700">
                                Preview & Rotate PDF Pages
                            </h1>

                            <button
                                onClick={rotateAllPages}
                                className="bg-violet-600 mx-auto w-full sm:w-[200px] py-3 text-xl rounded-lg font-semibold text-white flex items-center justify-center gap-4 hover:bg-violet-400"
                            >Rotate All</button>

                            <div className="w-full flex flex-wrap justify-center gap-10 md:border-2 md:px-10 md:py-14 md:rounded-xl">

                                {Array.from(new Array(numberOfPages), (el, index) => (
                                    <div
                                        key={index}
                                        className="border drop-shadow-2xl w-fit relative"
                                    >
                                        <Document
                                            file={pdfFile}
                                            onLoadSuccess={onDocumentLoadSuccess}
                                        >
                                            <Page
                                                pageNumber={index + 1}
                                                width={260}
                                                rotate={rotatePages[index + 1]}
                                                renderTextLayer={false}
                                                renderAnnotationLayer={false}
                                            />
                                        </Document>
                                        <div className="absolute top-2 left-2 w-8 h-8 bg-violet-500 rounded-full text-xl text-white flex justify-center items-center cursor-pointer">
                                            {index + 1}
                                        </div>
                                        <div
                                            onClick={() => rotatePage(index + 1)}
                                            className={`absolute top-2 right-2 w-8 h-8 bg-violet-500 rounded-full text-xl text-white flex justify-center items-center cursor-pointer`}
                                        >
                                            <BiRotateRight />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                    {pdfFile && (
                        <div id="rotate-pdfs" className="w-full">
                            <button
                                onClick={handleRotatePDF}
                                className="bg-violet-600 mx-auto w-full sm:w-[350px] py-4 text-xl rounded-lg font-semibold text-white flex items-center justify-center gap-4 hover:bg-violet-400"
                            >
                                {loader ? (
                                    <div
                                        className={`flex items-center text-white space-x-2 ${styles.loader}`}
                                    >
                                        <div className="">Downloading</div>
                                        <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                                        <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                                        <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                                    </div>
                                ) : (
                                    <>
                                        Download{" "}
                                        <FaDownload className="text-3xl" />
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>

                {
                    signaturePopup && <SignaturePopup onPopupClose={() => setSignaturePopup(false)} source="ROTATE_PDF" />
                }

                <div className="mt-20">
                    <div className="mb-20">
                        <PdfToolsLinks />
                    </div>
                    <Blog heading="Roate PDF Pages Tool FAQs" BlogData={BlogData} />
                </div>
            </div>
        </div>
    );
};

export default RotatePdf;
